import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { updateEntities, deleteEntity } from '@redux/entities'

import api from '@functions/api'

import type { PaginationRuleModel } from '@models/types'

import type { AppDispatch } from '@redux/store'
import type {
  Action, EntityOptions, Errors, FetchRequestPayload, RootReducerState,
} from '@redux/modules/types'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/paginationRule/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/paginationRule/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/paginationRule/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/paginationRule/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/paginationRule/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/paginationRule/CREATE_FAILURE'

const UPDATE_REQUEST = 'realbase/paginationRule/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/paginationRule/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/paginationRule/UPDATE_FAILURE'

const DELETE_REQUEST = 'realbase/paginationRule/DELETE_REQUEST'
const DELETE_SUCCESS = 'realbase/paginationRule/DELETE_SUCCESS'
const DELETE_FAILURE = 'realbase/paginationRule/DELETE_FAILURE'

export type PaginationRuleModuleState = {
  creating: boolean,
  deleting: boolean,
  errors: Errors,
  loaded: boolean,
  loadedForKeys: string[],
  loadedIds: number[],
  loading: boolean,
  updating: boolean,
}

// Initial State
const initialState: PaginationRuleModuleState = {
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loadedForKeys: [],
  loadedIds: [],
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}): Action {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
    loadedIds: payload.loadedIds,
  }
}

export function fetchSuccess(): Action {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []): Action {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest(): Action {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess(): Action {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []): Action {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest(): Action {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess(): Action {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors: Errors = []): Action {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest(): Action {
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess(): Action {
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors: Errors = []): Action {
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadPaginationRules(options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const loadedForKeys = [...getState().paginationRules.loadedForKeys]

    if (entityKey) loadedForKeys.push(entityKey)

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/pagination_rules.json', options)
      .then(({ data }: { data: PaginationRuleModel[] }) => {
        const normalizedJson = normalize(data, Schemas.PAGINATION_RULE_ARRAY)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function loadPaginationRule(paginationRuleId: number | string, options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const loadedForKeys = [...getState().paginationRules.loadedForKeys]

    if (entityKey) loadedForKeys.push(entityKey)

    // Set Loading
    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api(`/pagination_rules/${paginationRuleId}.json`, options)
      .then(({ data }: { data: PaginationRuleModel}) => {
        const normalizedJson = normalize(data, Schemas.PAGINATION_RULE)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createPaginationRule(paginationRule: PaginationRuleModel, options: EntityOptions = {}) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      pagination_rule: paginationRule,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/pagination_rules.json', options, config)
      .then(({ data }: { data: PaginationRuleModel }) => {
        const normalizedJson = normalize(data, Schemas.PAGINATION_RULE)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updatePaginationRule(paginationRule: PaginationRuleModel, options: EntityOptions = {}) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      pagination_rule: paginationRule,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/pagination_rules/${paginationRule.id}.json`, options, config)
      .then(({ data }: { data: PaginationRuleModel }) => {
        const normalizedJson = normalize(data, Schemas.PAGINATION_RULE)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function deletePaginationRule(paginationRule: PaginationRuleModel) {
  const config = {
    method: 'DELETE',
  }

  return (dispatch: AppDispatch) => {
    dispatch(deleteRequest())

    const promise = api(`/pagination_rules/${paginationRule.id}.json`, {}, config)
      .then(({ data }: { data: { id: number } }) => {
        const normalizedJson = normalize(data, Schemas.PAGINATION_RULE)
        // @ts-expect-error TS2345
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false as const, errors }
      })
    return promise
  }
}

// Reducer
export default function reducer(
  state: PaginationRuleModuleState = initialState,
  action: Action = { type: '' },
): PaginationRuleModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loadedIds: action.loadedIds || state.loadedIds,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors || [],
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors || [],
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
